import React from 'react';

import { get, isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { applicationActions } from '../../../redux/actions';
import { useIsMobileLayout, useRoutes } from '../../../hooks';
import { applicationSelectors, userSelectors } from '../../../redux/selectors';
import { createEnrollment } from '../../../services/apis/respondents/enrollments.respondents.api';

import RoleForm from '../../../containers/Forms/Respondents/RoleForm';

import Box from '../../../components/Styled/Box';
import FormCard from '../../../components/Cards/FormCard';
import Typography from '../../../components/Typography/Typography';

const RespondentRole = ({ isPublic }) => {
  const routes = useRoutes();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useIsMobileLayout();
  const { formatMessage: f } = useIntl();

  const currentApplication = useSelector(applicationSelectors.currentApplication);
  const assessmentType = get(currentApplication, 'assessment_type', '');
  const isOrganization = assessmentType === 'organization';

  const name = get(currentApplication, isOrganization ? 'company_name' : 'assessed_user.name', '');

  const userId = useSelector(userSelectors.userId);

  const enrollUser = async (applicationQuestionnaireToken) => {
    const { data } = await createEnrollment({
      params: `application_questionnaire_token=${applicationQuestionnaireToken}`,
      enrollment: { user_id: userId },
    });

    history.push(routes.respondent._questionnaire.replace(':id', data.id));
  };

  const enrollUserOpen = async (applicationQuestionnaireToken) => {
    const { data } = await createEnrollment({
      enrollment: { email: `respondente_externo_${uuid()}@example.com` },
      params: 'fields=hashid',
      applicationQuestionnaireToken,
      apiArgs: { apiOptions: { options: { disableHeaders: true } } },
    });

    history.push({
      pathname: routes.public.questionnaire,
      state: {
        enrollmentId: data.hashid,
        applicationQuestionnaireToken,
      },
    });
  };

  const onSubmit = (values) => {
    const applicationQuestionnaireToken = values.role.token;

    if (isPublic && !isOrganization) {
      history.push(routes.public.enrollQuestionnaire.replace(':token', applicationQuestionnaireToken));
    }

    if (isPublic && isOrganization) {
      enrollUserOpen(applicationQuestionnaireToken);
    }

    if (!isPublic) {
      enrollUser(applicationQuestionnaireToken);
    }
  };

  // caso não exista o currentApplication redireciona para home
  React.useEffect(() => {
    if (isEmpty(currentApplication)) {
      history.push(routes.start);
    }
  }, [currentApplication, history, routes.start]);

  // Remove o token do redux após a tela ser desmontada. Não será mais necessário e permitirá a navegação no resto do app.
  React.useEffect(() => () => {
    if (currentApplication.application_token) {
      dispatch(applicationActions.saveApplication({
        ...currentApplication,
        application_token: '',
      }));
    }
  }, [currentApplication, dispatch]);

  return isMobile
    ? (
      <Box width='100%' mt='calc(64px + 24px)'>
        <Box px='24px' pt='16px'>
          <Typography block variant='h4' tag='span' color='coldgrey600'>
            {
              isOrganization
                ? f({ id: 'pages.respondent_role.title_organization' })
                : f({ id: 'pages.respondent_role.title_individual' })
            }

            <Typography variant='h4' tag='span' color='purple100'>
              {` ${name}`}
            </Typography>
          </Typography>

          <Typography block mt={3} color='coldgrey600' variant='body'>
            {
              isOrganization
                ? f({ id: 'pages.respondent_role.subtitle_organization' })
                : f({ id: 'pages.respondent_role.subtitle_individual' }, { value: (name.split(' ') || [])[0] })
            }
          </Typography>
        </Box>
        <Box px='24px' pt='16px'>
          <RoleForm f={f} onSubmit={onSubmit} validateConfirmation={isPublic} />
        </Box>
      </Box>
    )
    : (
      <Box width='440px' display='flex' flexDirection='column' alignItems='center'>
        <Typography variant='h4' tag='span' color='coldgrey600'>
          {
            isOrganization
              ? f({ id: 'pages.respondent_role.title_organization' })
              : f({ id: 'pages.respondent_role.title_individual' })
          }

          <Typography variant='h4' tag='span' color='purple100'>
            {` ${name}`}
          </Typography>
        </Typography>

        <FormCard
          subtitle={
            isOrganization
              ? f({ id: 'pages.respondent_role.subtitle_organization' })
              : f({ id: 'pages.respondent_role.subtitle_individual' }, { value: (name.split(' ') || [])[0] })
          }
          cardProps={{
            margin: 'auto',
            mt: '32px',
            width: '100%',
            overflow: 'visible',
          }}
        >
          <RoleForm f={f} onSubmit={onSubmit} validateConfirmation={isPublic} />
        </FormCard>
      </Box>
    );
};

RespondentRole.propTypes = {
  isPublic: PropTypes.bool,
};

RespondentRole.defaultProps = {
  isPublic: false,
};

export default RespondentRole;
